import type { Task, Timesheet, KPIDef } from '@/types'
import { inPeriod, inDateRange, type FilterParams } from './filters'
import { isoWeek, isoMonth } from './dates'

export const SLA_THRESHOLDS: Record<string, number> = { MSO: 10, PSO: 20, Internal: 30 }

// Placeholder cost rate until real per-engineer rates exist — a flat €33/h applied to
// every logged hour, regardless of who logged it.
export const FLAT_HOURLY_COST_EUR = 33

// Monthly revenue per project (July 2026), EUR-converted — keyed by the exact Odoo
// project name so contracts under the same client stay separate (e.g. Aleph has three:
// IM/OM, FTE, DevOps FTE — collapsing them by client would blend three different
// contracts' margins into one meaningless number). Projects not listed here show
// "pending" instead of a guessed margin.
export const REVENUE_BY_PROJECT: Record<string, number> = {
  '[AWS] IM/OM - Vertical Fox LLC - 2025 - [02]': 3900.60,
  '[AWS] IM/OM - Kistler - 2025 - [02]': 4898.15,
  '[GCP] Heureka - IM Light - 2025 [02]': 2000.00,
  '[AWS] Incident Management - Eyeball - 2025 - [02]': 0,
  '[GCP] Sufio - Incident Management - 2025 - [02]': 2500.00,
  '[GCP] PagerDuty Usage - Sufio - 2025 - [02]': 255.31,
  '[GCP] IM/OM - GoOut - 2025 - [02]': 8000.00,
  '[AWS] IM/OM - Otherside Entertainment - 2025 [02]': 0,
  '[GCP] DevOps Support - OnSinch - 2025 [02]': 0,
  '[GCP] Incident Management - Popron - 2025 [02]': 1150.00,
  'GotPhoto [02]': 1300.00,
  'Aleph IM/OM': 8800.00,
  'Aleph FTE': 12000.00,
  'Aleph DevOps FTE': 23800.00,
  'Eesti [02]': 0,
  'Innogy Cognito consultations': 0,
  'Innogy API gateway': 0,
  'Innogy SES Migration': 831.44,
  'Livesport': 4698.00,
  'Microblink': 2405.90,
  'Auvious': 0,
}

// The revenue sheet's project names don't always match Odoo's live project names
// (renamed/reworded on one side or the other) — this maps the real Odoo name to the
// label used in REVENUE_BY_PROJECT. Two entries deliberately point at the same label
// (the two active "Eesti" projects) so their hours combine into one revenue line,
// since the revenue sheet doesn't split them. Verified against real activity: e.g.
// "[MSO][AWS] IM - Eyeball" has zero tasks/hours (a dead duplicate) and is
// intentionally left unmapped — "[MSO][AWS] Eyeball - IM" is the active one.
export const PROJECT_ALIASES: Record<string, string> = {
  '[MSO][AWS] IM/OM - VerticalFox': '[AWS] IM/OM - Vertical Fox LLC - 2025 - [02]',
  '[MSO][AWS] IM/OM - Kistler': '[AWS] IM/OM - Kistler - 2025 - [02]',
  '[MSO][GCP] IM Light - Heureka': '[GCP] Heureka - IM Light - 2025 [02]',
  '[MSO][AWS] Eyeball - IM': '[AWS] Incident Management - Eyeball - 2025 - [02]',
  '[MSO][GCP] IM - Sufio': '[GCP] Sufio - Incident Management - 2025 - [02]',
  '[MSO][AWS] IM/OM - GoOut': '[GCP] IM/OM - GoOut - 2025 - [02]',
  '[MSO][AWS] IM/OM - Otherside': '[AWS] IM/OM - Otherside Entertainment - 2025 [02]',
  '[MSO][AWS] DevOps Support - OnSinch': '[GCP] DevOps Support - OnSinch - 2025 [02]',
  '[MSO][GCP] IM - Popron Systems': '[GCP] Incident Management - Popron - 2025 [02]',
  '[MSO][AWS] GotPhoto WIZ Platform Mngmnt ': 'GotPhoto [02]',
  'Aleph Holding IM/OM': 'Aleph IM/OM',
  '[AWS][PSO] Aleph FTE': 'Aleph FTE',
  '[AWS] Aleph - DevOps FTE': 'Aleph DevOps FTE',
  '[PSO][AWS] Innogy - Cognito consultations': 'Innogy Cognito consultations',
  '[PSO][AWS] Innogy IaC Automation - API Gateway': 'Innogy API gateway',
  '[AWS][PSO] Innogy - SES migration': 'Innogy SES Migration',
  'Eesti- Presales': 'Eesti [02]',
  '[PSO][AWS] Eesti Interneti': 'Eesti [02]',
  '[PSO][AWS] Eesti Interneti Consultation': 'Eesti [02]',
}

export interface AccountMargin {
  client: string
  hours: number
  cost: number
  revenue: number | null
  margin: number | null
  marginPct: number | null
}

// Grouped by project name (aliased to the revenue sheet's label where one exists),
// matching the granularity revenue is actually tracked at — one row per contract, not
// per client. `timesheets` should already be scoped to the period being shown; cost
// is a flat-rate proxy (hours × FLAT_HOURLY_COST_EUR) since real per-engineer rates
// aren't available yet. Margin is the standard definition: (revenue − cost) / revenue
// × 100 — a percentage, not a raw € amount. Null (not 0% or Infinity) when revenue is
// 0 or unknown, since a margin percentage is meaningless without real revenue to
// divide by.
export function computeAccountMargins(timesheets: Timesheet[]): AccountMargin[] {
  const hoursByProj: Record<string, number> = {}
  timesheets.forEach(ts => {
    const key = PROJECT_ALIASES[ts.proj] ?? ts.proj
    hoursByProj[key] = (hoursByProj[key] || 0) + ts.hours
  })

  return Object.entries(hoursByProj).map(([proj, hours]) => {
    const cost = hours * FLAT_HOURLY_COST_EUR
    const revenue = REVENUE_BY_PROJECT[proj] ?? null
    const margin = revenue !== null ? revenue - cost : null
    const marginPct = revenue !== null && revenue > 0 ? (margin! / revenue) * 100 : null
    return { client: proj, hours, cost, revenue, margin, marginPct }
  }).sort((a, b) => b.hours - a.hours)
}

// For Internal-category tasks, `t.client` is often populated with Revolgy's own
// legal entity rather than being empty — grouping by the raw field then reports
// your own company name as if it were a client. Normalize to a plain "Internal"
// bucket instead, so drill-downs only name real external clients.
export function clientLabel(t: Task): string {
  return t.cat === 'Internal' ? 'Internal' : (t.client || 'Internal')
}

// `cat` (MSO/PSO/Internal) is assigned server-side by a strict name-prefix match
// (backend/app/config.py) — anything not starting with [MSO]/[PSO] defaults to
// "Internal", which silently swallows real customer engagements whose project name
// doesn't happen to follow that convention. Billable Utilization needs the actual
// business answer ("is a client paying for this hour"), not the prefix-match
// artifact, so it checks against that instead of `cat` alone.
//
// Confirmed 2026-08-19: these are real, billable customer work despite landing in
// the Internal bucket. "[AWS][PSO] Aleph FTE" even has PSO in the name — the prefix
// check just expects "[PSO][AWS]", not "[AWS][PSO]", and misses it.
const BILLABLE_PROJECT_OVERRIDES = new Set([
  'Aleph Holding IM/OM',
  '[AWS] Aleph - DevOps FTE',
  '[AWS][PSO] Aleph FTE',
])

// Shared by isBillable (timesheets) and Schedule Bot's task-facing column (tasks) —
// same business question ("is a client paying for this"), two different record
// types. Keeping one function means fixing the Aleph-project prefix miss here fixes
// it everywhere at once, instead of two definitions quietly drifting apart.
export function isClientFacingProject(cat: string, proj: string): boolean {
  if (cat === 'MSO' || cat === 'PSO') return true
  if (proj.startsWith('[Customers]')) return true
  return BILLABLE_PROJECT_OVERRIDES.has(proj)
}

export function isBillable(ts: Timesheet): boolean {
  return isClientFacingProject(ts.cat, ts.proj)
}

// Real customer-facing work that engineers have been asked to start logging under
// the dedicated "[Customers] AWS Services Team" project in Odoo, but haven't yet —
// so it's still sitting under internal catch-all projects. Confirmed 2026-08-19:
// retag it here, dashboard-only (nothing changes in Odoo), so Billable Utilization
// and the per-project breakdown already reflect where it actually belongs, rather
// than waiting on the team to start timesheeting against the new project. Remove
// these once that migration actually happens, or the hours will double-count under
// both the old and new project names.
const RETAG_TASK_IDS_AS_CUSTOMERS = new Set([
  1892, // "Customer-related internal meetings" (today logged under [Internal] Remangu - AWS Services)
  2148, // "Engineering support" (same project)
  2545, // "Renovate patching campaign" -- patching work done on client accounts
  2544, // "[Renovate patching campaign] Bring infrastructure up-to-date" -- same campaign
  2543, // "[Renovate patching campaign] Design and implement new Renovate process" -- same campaign
  8309, // "Renovate patching campaign" -- newer copy of the task still sitting under the Internal catch-all (8213 already lives in Customers)
  // EKS/GKE 1.35 upgrade cycle (Berkay, 2026-10-21) -- upgrades of client clusters logged under the internal catch-all.
  // 6279/6280 are the Infraops (own infrastructure) upgrades; included with the rest of the cycle, drop them if those should stay Internal.
  6271, // "EKS 1.35 upgrade" (parent)
  6272, // "Initial review of changes"
  6273, // "Verticalfox develop"
  6274, // "Verticalfox production"
  6275, // "Kistler develop"
  6277, // "Kistler staging"
  6278, // "Kistler production"
  6279, // "Infraops develop"
  6280, // "Infraops production"
  6281, // "[GKE 1.35 upgrade] Goout develop"
  6282, // "[GKE 1.35 upgrade] Goout production"
  6270, // "Deploy new monitoring stack to all customers"
  6287, // "Alerting priority and urgency configuration"
  6267, // "Test and configure Renovate with TF lock files"
  8158, // "Identity Center module" (AWS Platform modularization subtask)
  8159, // "Control Tower module" (same)
  8161, // "AWS Organizations module" (same)
  8162, // "Security module" (same)
  2198, // "Task triaging/preparation" (also has an empty placeholder task 8212 in the real Customers project already)
  8147, // "AWS Platform modularization" (parent task)
  8155, // "Prepare module versioning workflow" (AWS Platform modularization subtask)
  8156, // "Prepare documentation and repository settings" (same)
  8160, // "CloudTrail module" (same)
  8168, // "Backup module" (same)
  8171, // "Final QA" (same)
  6264, // "Sufio alert automation" -- client-specific work logged under the internal catch-all
  6132, // "Kubernetes monitoring stack review and improvements"
  5858, // "AWS quota monitoring solution"
  6099, // "Guardrails/AWS platform"
  5832, // "Create GCP quota monitoring module"
  5915, // "Alert investigation" (the one instance of this generic task name that sits under
        // the internal catch-all project -- every other "Alert investigation" task lives
        // under its own MSO client project already and is billable via cat already)
  3000, // "Incident handling"
  3071, // "[Incident handling] [Stage 1] Fix Pagerduty IaC"
  3072, // "[Incident handling] [Stage 1] Prepare customer onboarding checklists"
  3073, // "[Incident handling] [Stage 1] Choose the automation platform"
  3074, // "[Incident handling] [Stage 1] Prepare the relevant metrics for the analytics tool"
  3075, // "[Incident handling] [Stage 1] Prepare and document the processes"
  3076, // "[Incident handling] [Stage 1] Team training and handover"
  3077, // "[Incident handling] [Stage 1] Prepare the analytics tool dashboards"
  3078, // "[Incident handling] [Stage 1] Prepare Terraform code for the incident handling platform"
  3079, // "[Incident handling] [Stage 1] Design the automation rules schema"
  3080, // "[Incident handling] [Stage 1] Prepare the Lambda code"
  3081, // "[Incident handling] [Stage 1] Connect GCP/AWS notifications to our Pagerduty"
  3082, // "[Incident handling] [Stage 1] Testing and verification of the solution"
  3083, // "[Incident handling] [Stage 1] Design the process and place for new incident procedures (Known Alerts v2.0)"
  3086, // "[Incident handling] [Stage 1] Check customer onboarding checklist with existing customers"
  3087, // "[Incident handling] [Stage 1] Incident handling platform staging environment"
  3088, // "[Incident handling] [Stage 1] Prepare Step Functions state machine logic"
])
const RETAG_PROJECTS_AS_CUSTOMERS = new Set([
  '[Internal] AWS Services - Tickets/Issues', // ticket/alert triage
  '[Internal] AWS Services - On-Call Support', // on-call incident response
])
// Confirmed 2026-09-08: "Resell AWS accounts restructuring/cleanup" stays Internal --
// reseller/account admin, not client-billable work, despite sitting next to a lot of
// retagged items above.
const CUSTOMERS_PROJECT_NAME = '[Customers] AWS Services Team'

export function applyDashboardRetags(timesheets: Timesheet[]): Timesheet[] {
  return timesheets.map(ts => {
    if (RETAG_TASK_IDS_AS_CUSTOMERS.has(ts.task_id) || RETAG_PROJECTS_AS_CUSTOMERS.has(ts.proj)) {
      return { ...ts, proj: CUSTOMERS_PROJECT_NAME }
    }
    return ts
  })
}

// Groups tasks by an arbitrary key (client, engineer, ...) and returns the top N by
// count — used to name the likely driver(s) behind an aggregate metric moving, e.g.
// "mostly Kistler and Popron" instead of just "WIP is up."
export function topTaskGroups(tasks: Task[], keyFn: (t: Task) => string, n = 2): Array<{ key: string; count: number }> {
  const counts: Record<string, number> = {}
  tasks.forEach(t => {
    const k = keyFn(t)
    if (!k) return
    counts[k] = (counts[k] || 0) + 1
  })
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, n).map(([key, count]) => ({ key, count }))
}

// Recurring maintenance work, not real delivery work — excluded from SLA, capacity,
// and insight views everywhere in the app (shared so both pages agree on this).
export const isRecurring = (t: Task): boolean => !!t.tags?.includes('AWS Recurring')

export function computeSlaStatus(t: Task): { sla: number; slaPct: number } {
  const sla = SLA_THRESHOLDS[t.cat] || 30
  return { sla, slaPct: (t.age || 0) / sla * 100 }
}

export interface OverdueTask {
  task: Task
  daysOverdue: number
}

// Deadline-passed only. "SLA breach" (age vs a category threshold, via
// computeSlaStatus) is a different, deadline-independent idea — a task can breach
// it with no deadline set at all, which is exactly what "Deadlines" should not
// include. The Delivery page's own SLA table still uses computeSlaStatus directly.
// `activeTasks` should already exclude Done/Backlog/cancelled (useFilteredData().activeTasks).
export function computeOverdueTasks(activeTasks: Task[]): OverdueTask[] {
  const today = new Date().toISOString().slice(0, 10)
  return activeTasks
    .filter(t => t.deadline && t.deadline < today)
    .map(t => ({ task: t, daysOverdue: Math.round((Date.now() - new Date(t.deadline).getTime()) / 86400000) }))
    .sort((a, b) => b.daysOverdue - a.daysOverdue)
}

// Tasks that have been open a long time regardless of stage/status — a coarser
// signal than SLA (fixed per-category threshold) or "stuck" (time in current
// status only). `activeTasks` should already exclude Done/Backlog/cancelled.
export function computeLongRunningTasks(activeTasks: Task[], thresholdDays = 90): Task[] {
  return activeTasks
    .filter(t => (t.age || 0) > thresholdDays)
    .sort((a, b) => (b.age || 0) - (a.age || 0))
}

// `previous === 0` can't produce a ratio: null means "no prior baseline" (still
// notable if current isn't also 0), rather than a misleading 0%/Infinity.
export function pctDelta(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null
  return ((current - previous) / previous) * 100
}

export function isNotable(pctChange: number | null, thresholdPct: number): boolean {
  return pctChange === null || Math.abs(pctChange) >= thresholdPct
}

// Reconstructs how many tasks were open "as of" a given moment, purely from task
// fields — used to compare "WIP now" against "WIP `period` days ago" as two
// point-in-time counts.
export function computeOpenCount(tasks: Task[], asOf: Date): number {
  return tasks.filter(t => {
    const created = new Date(t.created)
    const closed = t.stage === 'Done' && t.last_stage ? new Date(t.last_stage) : null
    return created <= asOf && (!closed || closed > asOf) && t.stage !== 'Backlog'
  }).length
}

// % of done tasks (that had a deadline at all) which finished on or before it.
export function computeDeadlineAdherence(doneTasks: Task[]): number | null {
  const withDeadline = doneTasks.filter(t => t.deadline)
  if (withDeadline.length === 0) return null
  const onTime = withDeadline.filter(t => t.last_stage <= t.deadline).length
  return (onTime / withDeadline.length) * 100
}

export const WEEKLY_CAPACITY_HOURS = 30
export const WORKDAYS_PER_WEEK = 5
export const HOURS_PER_WORKDAY = WEEKLY_CAPACITY_HOURS / WORKDAYS_PER_WEEK

// Workdays remaining in the current Mon-Fri week, counting today. Weekends return 0 —
// there's nothing left in "this work week" once it's over.
export function remainingWorkdaysThisWeek(today = new Date()): number {
  const day = today.getDay() // 0 Sun .. 6 Sat
  if (day === 0 || day === 6) return 0
  return 6 - day
}

export interface EngineerCapacity {
  name: string
  tasks: Array<Task & {
    totalEstimation: number
    leftInEstimation: number | null
    workedThisWeek: number
    countsTowardCapacity: boolean
    subtaskCount: number
    childTotal: number
    childDone: number
  }>
  totalLeftInEstimation: number
  remainingCapacity: number
  freeHours: number
  loggedThisWeek: number
}

// Free capacity = hours left in the work week (remaining workdays × 6h/day) minus the
// sum of "Left in Estimation" across every assigned task NOT in Backlog (alloc_h −
// eff_h, floored at 0) — the remaining estimate on that task specifically, not its
// full original estimate. Backlog is excluded from the sum on purpose: a project like
// a multi-month server migration can have 50+ small Backlog tasks totaling hundreds of
// hours that were never meant to happen this week — only work that's actually started
// (any non-Backlog, non-Done stage) is a realistic claim on this week's time. Backlog
// tasks still show in the list (so the queue is visible), just don't count against
// the weekly number. "Worked This Week" (hours logged against that task this week) is
// shown per task for context but isn't subtracted again — it's already reflected in a
// lower "left in estimation," so subtracting both would double-count the same effort.
// A task with no estimate (alloc_h = 0) has unknown remaining hours (null), not 0 —
// 0 would wrongly read as "nothing left" when really there's just no estimate.
// `assignableTasks` should be every non-Done, non-cancelled, non-Blocked task (any
// other stage — Odoo's stage names vary a lot per project), recurring already
// excluded. `timesheets` should be unfiltered.
//
// Odoo subtasks are their own project.task records, and get created without an
// assignee far more often than top-level tasks do (e.g. a lead breaking a task into
// unassigned subtasks before handing them out) — an unassigned subtask's hours would
// otherwise vanish from capacity entirely, understating how much work is actually
// sitting on the parent task's owner. `subtaskPool` is searched for a parent task's
// unassigned (no users) children and their hours are folded into that parent's own
// Est/Worked/Left — it defaults to `assignableTasks` but callers that apply an
// engineer-specific filter upstream should pass a broader, non-engineer-filtered pool
// so a header engineer filter doesn't hide another engineer's unassigned subtasks
// out from under the parent task.
export function computeEngineerCapacity(
  engineers: string[],
  assignableTasks: Task[],
  timesheets: Timesheet[],
  currentWeekKey: string,
  subtaskPool: Task[] = assignableTasks,
): EngineerCapacity[] {
  const remainingCapacity = remainingWorkdaysThisWeek() * HOURS_PER_WORKDAY
  return engineers.map(name => {
    const tasks = assignableTasks
      .filter(t => t.users.includes(name))
      .map(t => {
        const unassignedChildren = subtaskPool.filter(c => c.parentId === t.id && c.users.length === 0)
        const childAlloc = unassignedChildren.reduce((a, c) => a + c.alloc_h, 0)
        const childEff = unassignedChildren.reduce((a, c) => a + c.eff_h, 0)
        const childTaskIds = unassignedChildren.map(c => c.id)

        const totalEstimation = t.alloc_h + childAlloc
        const totalEffective = t.eff_h + childEff
        const leftInEstimation = totalEstimation > 0 ? Math.max(totalEstimation - totalEffective, 0) : null

        const workedThisWeek = timesheets
          .filter(ts => (ts.task_id === t.id || childTaskIds.includes(ts.task_id))
            && ts.user === name && isoWeek(ts.date) === currentWeekKey)
          .reduce((a, ts) => a + ts.hours, 0)

        // All children (assigned or not) — a coarser "is this actually moving"
        // signal than Est/Worked/Left alone, for tasks that are really a pile of
        // subtasks (e.g. a 70-subtask migration) where the parent's own hours
        // don't tell you much.
        const allChildren = subtaskPool.filter(c => c.parentId === t.id)

        return {
          ...t,
          totalEstimation,
          leftInEstimation,
          workedThisWeek,
          countsTowardCapacity: t.stage !== 'Backlog',
          subtaskCount: unassignedChildren.length,
          childTotal: allChildren.length,
          childDone: allChildren.filter(c => c.stage === 'Done').length,
        }
      })

    const totalLeftInEstimation = tasks
      .filter(t => t.countsTowardCapacity)
      .reduce((a, t) => a + (t.leftInEstimation || 0), 0)
    const freeHours = remainingCapacity - totalLeftInEstimation

    // Total hours this engineer has logged this week, against anything at all —
    // not just the tasks listed above (they may log against Done work too). The
    // single clearest "are they actually active this week" signal: someone with
    // open work and 0h logged is either stalled or behind on their timesheet.
    const loggedThisWeek = timesheets
      .filter(ts => ts.user === name && isoWeek(ts.date) === currentWeekKey)
      .reduce((a, ts) => a + ts.hours, 0)

    return { name, tasks, totalLeftInEstimation, remainingCapacity, freeHours, loggedThisWeek }
  }).sort((a, b) => a.freeHours - b.freeHours)
}

export interface InsightTaskRef {
  id: number
  projId: number
  name: string
  meta: string
}

export interface Insight {
  key: string
  headline: string
  recommendation: string
  severity: number
  tasks: InsightTaskRef[]
}

// Concrete, current-state findings named against real tasks/engineers/projects — not
// period-over-period metric deltas (WIP/throughput/lead time/etc), which barely move
// week to week and don't tell anyone what to actually do. Each finding pairs a fact
// with a specific, immediately-actionable recommendation, plus `tasks` — the actual
// task(s) behind it (id + projId) so the caller can render a clickable link straight
// to Odoo instead of a name the reader has to go find themselves. Ranked by severity;
// callers should cap how many they render (top 3-5 is plenty — this isn't meant to
// be exhaustive, just the handful of things actually worth acting on today).
export function computeInsights(
  regularTasks: Task[],
  capacity: EngineerCapacity[],
  timesheets: Timesheet[],
  today = new Date(),
): Insight[] {
  const insights: Insight[] = []
  const push = (key: string, headline: string, recommendation: string, severity: number, tasks: InsightTaskRef[] = []) => {
    insights.push({ key, headline, recommendation, severity, tasks })
  }
  const ref = (t: Task, meta: string): InsightTaskRef => ({ id: t.id, projId: t.projId, name: t.name, meta })

  // 1. Blocked tasks stuck long enough to threaten whatever's downstream of them.
  const stuckBlocked = regularTasks
    .filter(t => t.stage.toLowerCase().includes('block') && (t.time_in_status || 0) >= 5)
    .sort((a, b) => (b.time_in_status || 0) - (a.time_in_status || 0))
  if (stuckBlocked.length > 0) {
    const shown = stuckBlocked.slice(0, 5)
    const rest = stuckBlocked.length > shown.length ? ` +${stuckBlocked.length - shown.length} more not shown.` : ''
    push('blocked',
      `${stuckBlocked.length} task${stuckBlocked.length > 1 ? 's have' : ' has'} been blocked 5+ business days.${rest}`,
      `Get an owner on the blocker today — every extra day it sits pushes anything downstream of it by the same amount.`,
      (shown[0].time_in_status || 0) + stuckBlocked.length,
      shown.map(t => ref(t, `${t.time_in_status}d blocked · ${t.users[0] || 'unassigned'}`)))
  }

  // 2. Capacity imbalance — name the specific task to move and who to move it to.
  const overloaded = capacity.filter(c => c.freeHours < -3).sort((a, b) => a.freeHours - b.freeHours)
  const free = capacity.filter(c => c.freeHours > 10).sort((a, b) => b.freeHours - a.freeHours)
  if (overloaded.length > 0 && free.length > 0) {
    const worst = overloaded[0]
    const target = free[0]
    const moveCandidate = worst.tasks
      .filter(t => t.countsTowardCapacity && !t.stage.toLowerCase().includes('block') && (t.leftInEstimation || 0) > 0)
      .sort((a, b) => (b.leftInEstimation || 0) - (a.leftInEstimation || 0))[0]
    if (moveCandidate) {
      push('capacity',
        `${worst.name} is ${Math.abs(worst.freeHours).toFixed(0)}h over capacity this week; ${target.name} has ${target.freeHours.toFixed(0)}h free.`,
        `Move this task from ${worst.name} to ${target.name}.`,
        Math.abs(worst.freeHours),
        [ref(moveCandidate, `${(moveCandidate.leftInEstimation || 0).toFixed(0)}h left · move to ${target.name}`)])
    }
  }

  // 3. Backlog pileup — too much queued work for one person to realistically clear.
  const backlogs = capacity.map(c => {
    const tasks = c.tasks.filter(t => !t.countsTowardCapacity)
    return { name: c.name, tasks, hours: tasks.reduce((a, t) => a + (t.leftInEstimation || 0), 0) }
  }).filter(c => c.hours >= 150).sort((a, b) => b.hours - a.hours)
  if (backlogs.length > 0) {
    const w = backlogs[0]
    const biggest = w.tasks.slice().sort((a, b) => (b.leftInEstimation || 0) - (a.leftInEstimation || 0)).slice(0, 5)
    push('backlogPileup',
      `${w.name} has ${w.tasks.length} Backlog tasks totaling ${w.hours.toFixed(0)}h queued — months of work for one person.`,
      `Run a triage session on ${w.name}'s backlog — re-scope, drop, or hand off chunks of it; it won't clear itself.`,
      w.hours,
      biggest.map(t => ref(t, `${(t.leftInEstimation || 0).toFixed(0)}h estimated · Backlog`)))
  }

  // 4. Timesheet/stall check — work marked "In Progress" with nothing logged this week.
  const stalledTasks: InsightTaskRef[] = []
  let stalledEngineerCount = 0
  capacity.forEach(c => {
    const inProg = c.tasks.filter(t => t.stage.toLowerCase().includes('progress'))
    const worked = inProg.reduce((a, t) => a + t.workedThisWeek, 0)
    if (inProg.length > 0 && worked === 0) {
      stalledEngineerCount++
      inProg.forEach(t => stalledTasks.push(ref(t, `${c.name} · 0h logged this week`)))
    }
  })
  if (stalledTasks.length > 0) {
    const shown = stalledTasks.slice(0, 5)
    const rest = stalledTasks.length > shown.length ? ` +${stalledTasks.length - shown.length} more not shown.` : ''
    push('stalled',
      `${stalledEngineerCount} engineer${stalledEngineerCount > 1 ? 's have' : ' has'} tasks marked "In Progress" with zero hours logged this week.${rest}`,
      `Check in — either the timesheet is behind, or the task isn't actually moving and its stage should reflect that.`,
      stalledTasks.length * 10,
      shown)
  }

  // 5. Thin or negative margin, from this calendar month's logged hours against
  // contracted revenue (not the header's period filter — margin math assumes a full
  // month, see computeAccountMargins). Project-level, so no single task to link.
  const monthKey = isoMonth(today.toISOString().slice(0, 10))
  const monthTS = timesheets.filter(ts => isoMonth(ts.date) === monthKey)
  const thinMargins = computeAccountMargins(monthTS)
    .filter((m): m is AccountMargin & { marginPct: number } => m.marginPct !== null && m.marginPct < 15)
    .sort((a, b) => a.marginPct - b.marginPct)
  if (thinMargins.length > 0) {
    const w = thinMargins[0]
    const state = w.marginPct < 0 ? 'is losing money' : 'is barely breaking even'
    push('margin',
      `"${w.client}" ${state} this month — ${w.marginPct.toFixed(0)}% margin on ${w.hours.toFixed(0)}h logged (€${w.cost.toFixed(0)} cost vs €${(w.revenue || 0).toFixed(0)} revenue).`,
      `Renegotiate the rate or cut scope on "${w.client}" — at the current flat cost basis it isn't paying for the hours going into it.`,
      15 - w.marginPct)
  }

  return insights.sort((a, b) => b.severity - a.severity)
}

// Shared by computeEngineerKPIs (per-engineer) and the Overview page's Insights
// section (team-wide), so the definition of "accurate" only lives in one place.
export function computeEstimationAccuracy(tasks: Task[]): number | null {
  const estTasks = tasks.filter(t => t.alloc_h > 0 && t.eff_h > 0)
  if (estTasks.length === 0) return null
  const accs = estTasks.map(t => Math.max(0, 100 - Math.abs(100 - (t.eff_h / t.alloc_h) * 100)))
  return accs.reduce((a, b) => a + b, 0) / accs.length
}

export const SCORE_TIERS = [
  { key: 'failing', label: 'Failing', color: 'text-red-800 bg-red-50 dark:text-red-300 dark:bg-red-950/50' },
  { key: 'needs-imp', label: 'Needs Imp.', color: 'text-amber-800 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/50' },
  { key: 'good', label: 'Good', color: 'text-emerald-800 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/50' },
  { key: 'very-good', label: 'Very Good', color: 'text-emerald-800 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/50' },
  { key: 'exemplary', label: 'Exemplary', color: 'text-emerald-800 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/50' },
]

export const KPI_DEFS: KPIDef[] = [
  { k: 'taskCompletion', label: 'Task Completion', unit: '%', thresholds: [80, 90, 95, 98], higherBetter: true },
  { k: 'slaAdherence', label: 'SLA Adherence', unit: '%', thresholds: [80, 90, 95, 100], higherBetter: true },
  { k: 'estAccuracy', label: 'Est. Accuracy', unit: '%', thresholds: [50, 70, 85, 95], higherBetter: true },
  { k: 'deadlineAdh', label: 'Deadline Adh.', unit: '%', thresholds: [60, 75, 85, 95], higherBetter: true },
  { k: 'firstResponse', label: '1st Response', unit: 'd', thresholds: [5, 3, 2, 1], higherBetter: false },
  { k: 'billableUtil', label: 'Billable Util.', unit: '%', thresholds: [40, 55, 65, 75], higherBetter: true },
  { k: 'meetingRatio', label: 'Meeting Ratio', unit: '%', thresholds: [50, 40, 30, 20], higherBetter: false },
  { k: 'backlogGrowth', label: 'Backlog Growth', unit: 'x', thresholds: [1.5, 1.2, 1.0, 0.8], higherBetter: false },
]

export function scoreTier(val: number | null, def: KPIDef): number {
  if (val === null || val === undefined || isNaN(val)) return -1
  const t = def.thresholds
  if (def.higherBetter) {
    if (val >= t[3]) return 4
    if (val >= t[2]) return 3
    if (val >= t[1]) return 2
    if (val >= t[0]) return 1
    return 0
  } else {
    if (val <= t[3]) return 4
    if (val <= t[2]) return 3
    if (val <= t[1]) return 2
    if (val <= t[0]) return 1
    return 0
  }
}

export function computeEngineerKPIs(
  eng: string,
  tasks: Task[],
  timesheets: Timesheet[],
  f: FilterParams,
  dateFrom?: string,
  dateTo?: string,
) {
  const useRange = dateFrom && dateTo
  const inP = useRange
    ? (ds: string) => inDateRange(ds, dateFrom!, dateTo!)
    : (ds: string) => inPeriod(ds, f)

  const engTasks = tasks.filter(t => {
    if (t.cancelled) return false
    if (!f.activeProjectIds.includes(t.projId)) return false
    if (f.category !== 'all' && t.cat !== f.category) return false
    return t.users.includes(eng)
  })

  const engTS = timesheets.filter(ts => {
    if (f.category !== 'all' && ts.cat !== f.category) return false
    return ts.user === eng
  })

  const periodTS = engTS.filter(ts => inP(ts.date))
  const doneTasks = engTasks.filter(t => t.stage === 'Done' && inP(t.last_stage))
  const activeTasks = engTasks.filter(t => t.stage !== 'Done' && t.stage !== 'Backlog' && t.stage !== 'Cancelled')

  const totalRelevant = doneTasks.length + activeTasks.length
  const taskCompletion = totalRelevant > 0 ? (doneTasks.length / totalRelevant) * 100 : null

  let slaOk = 0
  doneTasks.forEach(t => {
    const sla = SLA_THRESHOLDS[t.cat] || 30
    if (t.lead !== null && t.lead <= sla) slaOk++
  })
  const slaAdherence = doneTasks.length > 0 ? (slaOk / doneTasks.length) * 100 : null

  const estTasks = doneTasks.filter(t => t.alloc_h > 0 && t.eff_h > 0)
  const estAccuracy = computeEstimationAccuracy(doneTasks)

  const withDl = doneTasks.filter(t => t.deadline)
  let dlOk = 0
  withDl.forEach(t => { if (t.last_stage <= t.deadline) dlOk++ })
  const deadlineAdh = withDl.length > 0 ? (dlOk / withDl.length) * 100 : null

  const withAssign = engTasks.filter(t => t.created && t.assigned && inP(t.created))
  let firstResponse: number | null = null
  if (withAssign.length > 0) {
    const diffs = withAssign.map(t => Math.max(0, (new Date(t.assigned).getTime() - new Date(t.created).getTime()) / 864e5))
    firstResponse = diffs.reduce((a, b) => a + b, 0) / diffs.length
  }

  const totalH = periodTS.reduce((a, ts) => a + ts.hours, 0)
  const billH = periodTS.filter(isBillable).reduce((a, ts) => a + ts.hours, 0)
  const billableUtil = totalH > 0 ? (billH / totalH) * 100 : null

  const meetH = periodTS.filter(ts => ts.is_meeting).reduce((a, ts) => a + ts.hours, 0)
  const meetingRatio = totalH > 0 ? (meetH / totalH) * 100 : null

  const createdInPeriod = engTasks.filter(t => inP(t.created)).length
  const backlogGrowth = doneTasks.length > 0 ? createdInPeriod / doneTasks.length : null

  return {
    taskCompletion, slaAdherence, estAccuracy, deadlineAdh,
    firstResponse, billableUtil, meetingRatio, backlogGrowth,
    _done: doneTasks.length, _active: activeTasks.length, _totalH: totalH,
    _estTaskCount: estTasks.length, _dlTaskCount: withDl.length,
    _doneTasks: doneTasks, _activeTasks: activeTasks, _estTasks: estTasks,
  }
}
